import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PencilSimple, Trash } from "@phosphor-icons/react";
import { motion } from "framer-motion";

// Draggable Floor Editor Component — canvas grows to fit the furthest
// table so a large restaurant fits without cramming.
const DraggableFloorEditor = ({ tables, onDrag, onDelete, onUpdate, isAdmin }) => {
  const containerRef = useRef(null);
  const [editing, setEditing] = useState(null); // table object being edited
  const [editForm, setEditForm] = useState({ name: "", seats: 4, shape: "square", size_scale: 1.0 });

  const openEdit = (table) => {
    setEditing(table);
    setEditForm({
      name: table.name || "",
      seats: table.seats || 4,
      shape: table.shape || "square",
      size_scale: table.size_scale ?? 1.0,
    });
  };

  const closeEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing) return;
    const updates = {
      name: editForm.name.trim() || editing.name,
      seats: Math.max(1, parseInt(editForm.seats, 10) || 1),
      shape: editForm.shape,
      size_scale: Math.max(0.5, Math.min(2.5, parseFloat(editForm.size_scale) || 1.0)),
    };
    await onUpdate(editing.id, updates);
    toast.success("Table updated");
    closeEdit();
  };

  const bumpSize = async (table, delta) => {
    const next = Math.max(0.5, Math.min(2.5, (table.size_scale ?? 1.0) + delta));
    await onUpdate(table.id, { size_scale: parseFloat(next.toFixed(2)) });
  };

  const getTableSize = (shape, seats, scale = 1.0) => {
    const base = 70 * (scale || 1);
    const mult = Math.min(seats / 4, 1.5);
    switch (shape) {
      // Round tables share the rectangle's long-side dimension so labels fit.
      case "round": return { width: base * 1.5 * mult, height: base * 1.5 * mult };
      case "rectangle": return { width: base * 1.5 * mult, height: base * mult };
      default: return { width: base * mult, height: base * mult };
    }
  };

  const getShapeClass = (shape) => shape === "round" ? "rounded-full" : "rounded-lg";

  // Inner canvas grows with the furthest table so the editor can hold a
  // large restaurant's worth of tables, scrolled both horizontally and
  // vertically.
  const canvasMinWidth = 1600;
  const canvasMinHeight = 1200;
  const canvasWidth = tables.reduce((max, t) => {
    const s = getTableSize(t.shape, t.seats, t.size_scale);
    return Math.max(max, (t.position?.x || 50) + s.width + 200);
  }, canvasMinWidth);
  const canvasHeight = tables.reduce((max, t) => {
    const s = getTableSize(t.shape, t.seats, t.size_scale);
    return Math.max(max, (t.position?.y || 50) + s.height + 200);
  }, canvasMinHeight);

  return (
    <>
      <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="floor-editor">
        <div className="overflow-auto" style={{ height: 500 }} data-testid="floor-editor-viewport">
          <div
            ref={containerRef}
            className="relative"
            style={{
              width: canvasWidth,
              height: canvasHeight,
              backgroundImage: "radial-gradient(circle, #D4D0C8 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
            data-testid="floor-editor-canvas"
          >
            {tables.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">No tables in this room</div>
            ) : (
              tables.map((table) => {
                const size = getTableSize(table.shape, table.seats, table.size_scale);
                return (
                  <motion.div
                    key={table.id}
                    drag={isAdmin}
                    dragMomentum={false}
                    dragElastic={0}
                    onDragEnd={(e, info) => {
                      // Delta-based positioning: use framer-motion's drag
                      // offset (start→end vector) so the table lands where
                      // the user actually dragged it, regardless of where
                      // on the table they grabbed. Using ``info.point``
                      // caused a teleport-to-cursor bug when the drag was
                      // short (the table would snap to the pointer location
                      // minus half its width, jumping to the top-right).
                      const currentX = table.position?.x || 50;
                      const currentY = table.position?.y || 50;
                      const slack = 500;
                      const newX = Math.max(0, Math.min(currentX + info.offset.x, canvasWidth + slack - size.width));
                      const newY = Math.max(0, Math.min(currentY + info.offset.y, canvasHeight + slack - size.height));
                      onDrag(table.id, { x: newX, y: newY });
                    }}
                    initial={false}
                    animate={{ x: table.position?.x || 50, y: table.position?.y || 50 }}
                    className={`absolute ${getShapeClass(table.shape)} bg-white border-2 border-border flex flex-col items-center justify-center shadow-sm hover:shadow-md transition-shadow ${isAdmin ? "cursor-move" : "cursor-default"} group`}
                    style={{ width: size.width, height: size.height, left: 0, top: 0 }}
                    data-testid={`editor-table-${table.id}`}
                    onDoubleClick={() => isAdmin && openEdit(table)}
                  >
                    <span className="font-heading text-xs font-medium">{table.name}</span>
                    <span className="text-[10px] text-muted-foreground">{table.seats}s</span>
                    {isAdmin && (
                      <>
                        {/* Hover toolbar: size - / + / edit / delete */}
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-card border border-border rounded-full shadow-sm px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => bumpSize(table, -0.1)}
                            className="w-5 h-5 text-xs rounded-full hover:bg-muted text-muted-foreground"
                            title="Shrink"
                            data-testid={`editor-shrink-${table.id}`}
                          >−</button>
                          <button
                            onClick={() => bumpSize(table, 0.1)}
                            className="w-5 h-5 text-xs rounded-full hover:bg-muted text-muted-foreground"
                            title="Enlarge"
                            data-testid={`editor-grow-${table.id}`}
                          >+</button>
                          <button
                            onClick={() => openEdit(table)}
                            className="w-5 h-5 text-[10px] rounded-full hover:bg-muted text-muted-foreground flex items-center justify-center"
                            title="Edit"
                            data-testid={`editor-edit-${table.id}`}
                          >
                            <PencilSimple size={10} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); onDelete(table.id); }}
                            className="w-5 h-5 text-[10px] rounded-full hover:bg-destructive/10 text-destructive flex items-center justify-center"
                            title="Delete"
                            data-testid={`delete-editor-table-${table.id}`}
                          >
                            <Trash size={10} />
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent data-testid="edit-table-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Edit Table</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-table-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Seats</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={editForm.seats}
                  onChange={(e) => setEditForm({ ...editForm, seats: e.target.value })}
                  data-testid="edit-table-seats"
                />
              </div>
              <div className="space-y-2">
                <Label>Shape</Label>
                <Select value={editForm.shape} onValueChange={(v) => setEditForm({ ...editForm, shape: v })}>
                  <SelectTrigger data-testid="edit-table-shape"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="square">Square</SelectItem>
                    <SelectItem value="round">Round</SelectItem>
                    <SelectItem value="rectangle">Rectangle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Size</Label>
                <span className="text-xs text-muted-foreground font-mono">
                  {Math.round(parseFloat(editForm.size_scale || 1) * 100)}%
                </span>
              </div>
              <input
                type="range"
                min={50}
                max={250}
                step={5}
                value={Math.round(parseFloat(editForm.size_scale || 1) * 100)}
                onChange={(e) => setEditForm({ ...editForm, size_scale: Number(e.target.value) / 100 })}
                className="w-full accent-primary"
                data-testid="edit-table-size"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>Small</span>
                <span>Default</span>
                <span>Large</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEdit}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="save-edit-table-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DraggableFloorEditor;

